import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { Store, Search, ExternalLink, Check, X, Eye, AlertCircle } from 'lucide-react';
import { useNotifications } from '../../contexts/NotificationContext';
import api from '../../utils/api';

const AdminResellerShops = () => {
  const { refreshPendingShops } = useNotifications();
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [updatingShop, setUpdatingShop] = useState(null);

  useEffect(() => {
    fetchShops();
  }, []);

  const fetchShops = async () => {
    try {
      const response = await api.get('/admin/reseller-shops');
      setShops(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Shops');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (shopId, currentStatus) => {
    setUpdatingShop(shopId);
    try {
      await api.put(`/admin/reseller-shops/${shopId}/status`, { is_active: !currentStatus });
      toast.success(!currentStatus ? 'Shop freigegeben – der Händler ist jetzt aktiv' : 'Shop deaktiviert');
      fetchShops();
      refreshPendingShops();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    } finally {
      setUpdatingShop(null);
    }
  };

  const filteredShops = shops
    .filter(shop =>
      shop.shop_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shop.shop_slug?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shop.owner?.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shop.owner?.email?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => (a.is_active === b.is_active ? 0 : a.is_active ? 1 : -1));

  const pendingCount = shops.filter(s => !s.is_active).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Store className="h-5 w-5" />
            Reseller-Shops ({shops.length})
          </h2>
          <p className="text-gray-500 text-sm">
            Verwalten Sie alle B2C-Shops der Barbershops
          </p>
          {pendingCount > 0 && (
            <div className="mt-2 flex items-center gap-2 bg-amber-50 border border-amber-300 text-amber-800 px-3 py-2 rounded-md text-sm font-medium" data-testid="pending-shops-hint">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {pendingCount} {pendingCount === 1 ? 'neuer Händler wartet' : 'neue Händler warten'} auf Freigabe
            </div>
          )}
        </div>
        <div className="relative w-full md:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Shop suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="ps-10"
          />
        </div>
      </div>

      {filteredShops.length === 0 ? (
        <div className="bg-white border border-border rounded-lg p-12 text-center">
          <Store className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium">Keine Shops gefunden</h3>
          <p className="text-gray-500">
            {searchTerm ? 'Keine Shops entsprechen Ihrer Suche' : 'Noch keine Reseller-Shops erstellt'}
          </p>
        </div>
      ) : (
        <div className="bg-white border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-start px-4 py-3 text-sm font-medium text-gray-600">Shop</th>
                  <th className="text-start px-4 py-3 text-sm font-medium text-gray-600">Besitzer</th>
                  <th className="text-start px-4 py-3 text-sm font-medium text-gray-600">Kontakt</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Status</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Aktionen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredShops.map(shop => (
                  <tr key={shop.id} className={shop.is_active ? 'hover:bg-gray-50' : 'bg-amber-50/60 hover:bg-amber-50'}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{shop.shop_name}</div>
                      <div className="text-sm text-gray-500">/shop/{shop.shop_slug}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{shop.owner?.company_name || '-'}</div>
                      <div className="text-sm text-gray-500">{shop.owner?.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm">{shop.contact_name || '-'}</div>
                      <div className="text-sm text-gray-500">{shop.contact_phone || '-'}</div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        shop.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-amber-100 text-amber-800'
                      }`}>
                        {shop.is_active ? 'Aktiv' : 'Freigabe ausstehend'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(`/shop/${shop.shop_slug}`, '_blank')}
                          disabled={!shop.is_active}
                          title="Shop ansehen"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant={shop.is_active ? "destructive" : "default"}
                          size="sm"
                          onClick={() => handleToggleStatus(shop.id, shop.is_active)}
                          disabled={updatingShop === shop.id}
                          data-testid={`toggle-shop-status-${shop.id}`}
                        >
                          {shop.is_active ? <X className="h-4 w-4" /> : (
                            <span className="flex items-center gap-1">
                              <Check className="h-4 w-4" /> Freigeben
                            </span>
                          )}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminResellerShops;
